import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';
import './PokemonBuilder.css';

const PokemonBuilder = () => {
  const [itemSprites, setItemSprites] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const items = [
          'HP Up',
          'Protein',
          'Iron',
          'Calcium',
          'Zinc',
          'Carbos',
          'Adamant Mint',
          'Normal Tera Shard',
          'TM001',
          'Mirror Herb',
          'Bottle Cap',
          'Gold Bottle Cap',
          'Ability Capsule',
          'Ability Patch'
        ];

        const responses = await Promise.all(
          items.map(item => 
            fetch(`${API_URL}/items/${encodeURIComponent(item)}`).then(res => res.json())
          )
        );

        const sprites = {};
        responses.forEach((response, index) => {
          if (response && response.sprite) {
            sprites[items[index]] = response.sprite;
          }
        });
        
        setItemSprites(sprites);
      } catch (error) {
        console.error('Error fetching items:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchItems();
  }, []);

  if (loading) {
    return <div className="loading">Loading items...</div>;
  }

  return (
    <div className="pokemon-builder">
      <div className="builder-header">
        <h2>Building Your Perfect Pokémon</h2>
        <p>A comprehensive guide to competitive Pokémon preparation in Scarlet & Violet</p>
      </div>

      <div className="builder-grid">
        <div className="guide-card vitamins">
          <div className="card-header">
            <img src={itemSprites['HP Up']} alt="HP Up" className="item-sprite" />
            <h3>Vitamins & EV Training</h3>
          </div>
          <div className="vitamin-grid">
            <div className="vitamin-item">
              <img src={itemSprites['HP Up']} alt="HP Up" />
              <span>HP Up</span>
              <small>HP EVs</small>
            </div>
            <div className="vitamin-item">
              <img src={itemSprites['Protein']} alt="Protein" />
              <span>Protein</span>
              <small>Attack EVs</small>
            </div>
            <div className="vitamin-item">
              <img src={itemSprites['Iron']} alt="Iron" />
              <span>Iron</span>
              <small>Defense EVs</small>
            </div>
            <div className="vitamin-item">
              <img src={itemSprites['Calcium']} alt="Calcium" />
              <span>Calcium</span>
              <small>Sp. Atk EVs</small>
            </div>
            <div className="vitamin-item">
              <img src={itemSprites['Zinc']} alt="Zinc" />
              <span>Zinc</span>
              <small>Sp. Def EVs</small>
            </div>
            <div className="vitamin-item">
              <img src={itemSprites['Carbos']} alt="Carbos" />
              <span>Carbos</span>
              <small>Speed EVs</small>
            </div>
          </div>
          <div className="card-info">
            <p><strong>Location:</strong> Any Delibird Presents shop in major cities</p>
            <p><strong>Cost:</strong> 10,000₽ each</p>
            <p><strong>Limit:</strong> Up to 26 of each vitamin per Pokémon</p>
          </div>
        </div>

        <div className="guide-card tera">
          <div className="card-header">
            <img src={itemSprites['Normal Tera Shard']} alt="Tera Shard" className="item-sprite" />
            <h3>Tera Type Change</h3>
          </div>
          <div className="tera-content">
            <div className="step-list">
              <div className="step">
                <span className="step-number">1</span>
                <p>Complete the main story to unlock Tera Raids</p>
              </div>
              <div className="step">
                <span className="step-number">2</span>
                <p>Collect Tera Shards from Tera Raids</p>
              </div>
              <div className="step">
                <span className="step-number">3</span>
                <p>Visit Treasure Eatery in Medali</p>
              </div>
              <div className="step">
                <span className="step-number">4</span>
                <p>Spend 50 Tera Shards to change type</p>
              </div>
            </div>
          </div>
        </div>

        <div className="guide-card natures">
          <div className="card-header">
            <img src={itemSprites['Adamant Mint']} alt="Nature Mint" className="item-sprite" />
            <h3>Nature Change</h3>
          </div>
          <div className="nature-content">
            <div className="mint-info">
              <img src={itemSprites['Adamant Mint']} alt="Nature Mint" className="mint-image" />
              <div>
                <p><strong>Location:</strong> Cascarrafa, Levincia, Mesagoza, Montenevera</p>
                <p><strong>Cost:</strong> 20,000 each</p>
                <p><strong>Effect:</strong> Changes stat calculation without changing original nature</p>
              </div>
            </div>
          </div>
        </div>

        <div className="guide-card moves">
          <div className="card-header">
            <img src={itemSprites['TM001']} alt="TM" className="item-sprite" />
            <h3>TM Learning</h3>
          </div>
          <div className="tm-content">
            <div className="tm-steps">
              <div className="tm-step">
                <img src={itemSprites['TM001']} alt="TM Machine" />
                <div>
                  <h4>TM Machine</h4>
                  <p>Visit any Pokémon Center to access the TM Machine</p>
                </div>
              </div>
              <div className="tm-step">
                <img src={itemSprites['TM001']} alt="TM" />
                <div>
                  <h4>Materials & LP</h4>
                  <p>Collect materials from wild Pokémon and use LP to craft TMs</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="guide-card mirror-herb">
          <div className="card-header">
            <img src={itemSprites['Mirror Herb']} alt="Mirror Herb" className="item-sprite" />
            <h3>Move Inheritance</h3>
          </div>
          <div className="mirror-content">
            <div className="mirror-info">
              <img src={itemSprites['Mirror Herb']} alt="Mirror Herb" className="mirror-image" />
              <div className="mirror-steps">
                <p><strong>Location:</strong> Casseroya Lake area</p>
                <p><strong>Effect:</strong> Allows Pokémon to learn moves from others in your party</p>
                <div className="strategy-steps">
                  <div className="strategy-step">
                    <span className="step-number">1</span>
                    <p>Find a Pokémon with the move</p>
                  </div>
                  <div className="strategy-step">
                    <span className="step-number">2</span>
                    <p>Give Mirror Herb to recipient</p>
                  </div>
                  <div className="strategy-step">
                    <span className="step-number">3</span>
                    <p>Ensure an empty move slot</p>
                  </div>
                  <div className="strategy-step">
                    <span className="step-number">4</span>
                    <p>Have a picnic together</p>
                  </div>
                  <div className="strategy-step">
                    <span className="step-number">5</span>
                    <p>Verify move was learned</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="guide-card stats">
          <div className="card-header">
            <img src={itemSprites['Bottle Cap']} alt="Bottle Cap" className="item-sprite" />
            <h3>IV & EV Optimization</h3>
          </div>
          <div className="stats-content">
            <div className="bottle-caps">
              <h4>Hyper Training</h4>
              <div className="cap-types">
                <div className="cap-item">
                  <img src={itemSprites['Bottle Cap']} alt="Bottle Cap" className="cap-image" />
                  <div>
                    <p><strong>Regular Bottle Cap</strong></p>
                    <p>Maximizes one IV stat</p>
                  </div>
                </div>
                <div className="cap-item">
                  <img src={itemSprites['Gold Bottle Cap']} alt="Gold Bottle Cap" className="cap-image" />
                  <div>
                    <p><strong>Gold Bottle Cap</strong></p>
                    <p>Maximizes all IV stats</p>
                  </div>
                </div>
              </div>
              <div className="requirements">
                <p><strong>Requirements:</strong></p>
                <ul>
                  <li>Pokémon must be level 50</li>
                  <li>Visit the Hyper Training NPC in Naranja/Uva Academy's Staff Break Room</li>
                  <li>Regular Bottle Caps can be obtained from:</li>
                  <ul>
                    <li>Delibird Presents (20,000 each)</li>
                    <li>5+★ Tera Raid rewards</li>
                    <li>Item Printer</li>
                  </ul>
                </ul>
              </div>
            </div>

          </div>
        </div>

        <div className="guide-card material-exchange">
          <div className="card-header">
            <img src={itemSprites['TM001']} alt="Item Printer" className="item-sprite" />
            <h3>Item Printer</h3>
          </div>
          <div className="exchange-content">
            <div className="exchange-info">
              <div>
                <p><strong>Location:</strong> BP League Room at Blueberry Academy</p>
                <p><strong>Cost:</strong> BP + Pokémon Materials</p>
                <p><strong>Potential Rewards:</strong></p>
                <ul>
                  <li>Ability Capsules</li>
                  <li>Bottle Caps</li>
                  <li>Nature Mints</li>
                  <li>Rare Candies</li>
                  <li>TMs</li>
                  <li>And more!</li>
                </ul>
                <p><strong>Tip:</strong> A great way to convert excess Pokémon materials and BP into valuable competitive items</p>
              </div>
            </div>
          </div>
        </div>

        <div className="guide-card abilities">
          <div className="card-header">
            <img src={itemSprites['Ability Capsule']} alt="Ability Items" className="item-sprite" />
            <h3>Ability Changes</h3>
          </div>
          <div className="ability-content">
            <div className="ability-info">
              <div>
                <div className="ability-types">
                  <h4>Understanding Abilities</h4>
                  <ul>
                    <li><strong>Standard Abilities:</strong> Most Pokémon have two possible standard abilities</li>
                    <li><strong>Hidden Ability:</strong> A special third ability some Pokémon can have</li>
                  </ul>
                </div>

                <div className="ability-items">
                  <div className="ability-item">
                    <h4>Ability Capsule</h4>
                    <img src={itemSprites['Ability Capsule']} alt="Ability Capsule" className="item-sprite" />
                    <ul>
                      <li>Switches between standard abilities only</li>
                      <li>Cannot access hidden ability</li>
                    </ul>
                  </div>

                  <div className="ability-item">
                    <h4>Ability Patch</h4>
                    <img src={itemSprites['Ability Patch']} alt="Ability Patch" className="item-sprite" />
                    <ul>
                      <li>Changes to hidden ability</li>
                    </ul>
                  </div>
                </div>

             </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PokemonBuilder; 